// @flow

import _ from 'lodash';

import FormaticConfig from 'app/common/FormaticConfig';
import NodeLabels from 'app/beta-editor/NodeLabels';
import NodeUtils from 'app/beta-editor/utils/NodeUtils';
import _str from 'app/common/underscore-string';
import servicesWithUnmappedFields from 'app/beta-editor/constants/servicesWithUnimportantFields';
import { getAncestors } from 'app/beta-editor/utils/NodeTrees';
import { getStepIndex } from 'app/beta-editor/utils/FlowUtils';
import {
  cleanObjectOfEmptyValues,
  exists,
  get,
  superHumanize,
} from 'app/common/CommonUtils';
import {
  getPathAttrs,
  getPathSelectedBackgroundColor,
  getRootNodeOfPath,
  getStepLabel,
  isNestedPath,
  isNodeInPath,
  isPathNode,
  isPathsGroupNode,
} from 'app/beta-editor/utils/PathUtils';

import type { Field, FieldChoice, Give } from 'app/beta-editor/types';
import type { FieldOrFunctionSchema } from 'app/developer-v3/platformSchema/field';
import type {
  Flow,
  FlowNode,
  FlowNodeType,
  NodeMeta,
} from 'app/beta-editor/flowState';
import type { ID } from 'app/common/types';
import type { ServicesMap } from 'app/service/types';

export const StaticReplaceChoices = [
  {
    value: '///loading///',
  },
];

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const transformGives = (gives: Give[]) => {
  return _.map(gives, give => {
    give = _.extend({}, give);
    give.original_key = give.key;
    give.key = `${give.node_id}__${give.key}`;
    return give;
  });
};

const humanizeGiveLabel = (label?: string) =>
  superHumanize((label || '').replace(/^\d+__/, ''));

const giveLabel = (give: Give) => {
  return give.label || humanizeGiveLabel(give.original_key || give.key);
};

/*
  Formats a sample value.
  If it's an array, returns comma-separated value. Truncates to limit
  characters, with ellipsis (...) at the end.
*/
const formatSampleValue = (
  value: string | Array<any> | boolean,
  limit: number = 2000
) => {
  let stringValue = value;
  if (Array.isArray(value)) {
    stringValue = value
      .map(thing => (_.isString(thing) ? thing : JSON.stringify(thing)))
      .join(', ');
  } else if (_.isBoolean(value)) {
    stringValue = String(value);
  }
  return _str.truncate(stringValue, limit, '...');
};

/*
   Formats a give's zap_meta_sample value.
 */
const formatSampleValueOfGive = (give: Give, limit: number = 2000) =>
  give ? formatSampleValue(give.zap_meta_sample, limit) : '';

const givesToReplaceChoices = (flow: Flow, node: FlowNode, gives: Give[]) => {
  return _.map(gives, give => {
    const parammap = get(node, ['meta', 'parammap'], {});
    const label = parammap[give.key] || giveLabel(give);

    return {
      value: give.key,
      label,
      sample: formatSampleValueOfGive(give),
    };
  });
};

const givesToChoices = (gives: Give[]) => {
  // slightly different in sample to givesToReplaceChoices
  return _.map(gives, give => {
    return {
      value: give.key,
      label: giveLabel(give),
      sample: formatSampleValueOfGive(give) || give.key,
    };
  });
};

const givesLabelMap = (gives: Give[]) => {
  return _.fromPairs(_.map(gives, give => [give.key, giveLabel(give)]));
};

const hasGivesPrefill = (field: Field) => field.prefill === 'gives';

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const injectReplaceChoices = (field: Field, replaceChoices: FieldChoice[]) => {
  // replaceChoices == gives!

  if (field.type === 'dict') {
    // we presume dict never has choices/dynamic dropdown...
    field.itemFields = [
      {
        type: 'pretty-text', // pretty sure this should be unicode
        replaceChoices,
        isAccordion: true,
      },
    ];
  } else if (field.type === 'list' && !field.choices) {
    // Formatic doesn't pass replaceChoices onto children
    // so we must move replaceChoices to list's children
    field.itemFields = [
      {
        type: 'unicode',
        shouldHideLabel: true,
        replaceChoices,
        isAccordion: true,
      },
    ];
  } else if (!field.prefill && !field.choices) {
    // a fallback for "normal" fields
    field.replaceChoices = replaceChoices;
    field.isAccordion = true;
  }

  return field;
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

// Put Zapier grouped fields into a nested "fields" field.
const groupFields = function(fields) {
  const groupedFields = [];
  const groups = {};

  fields.forEach(function(field) {
    if (field.parent_key) {
      let group = groups[field.parent_key];

      if (!group) {
        group = {
          type: 'fields',
          label: superHumanize(field.parent_key),
          fields: [],
          isGroup: true,
          // First field from group dictates whether the group is search or write.
          from_search: field.from_search,
          from_write: field.from_write,
          key: '',
        };
        if (field.parent_key) {
          groups[field.parent_key] = group;
        }
        groupedFields.push(group);
      }

      group.fields.push(field);
    } else {
      groupedFields.push(field); // top level field
    }
  });

  return groupedFields;
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

/* take a list of fields and convert them into a more formatic friendly representation
 * is ready for injection of dynamic choices after the fact
 */
const normalizeFieldsForFormatic = (fields: Field[]): Field[] => {
  const formaticFields = _.map(fields, field => {
    field = _.extend({}, field);

    // customer apps often return type as 'boolean' instead of 'bool'; normalize here.
    if (field.type === 'boolean') {
      field.type = 'bool';
    }

    field.zapierType = field.type;

    if (
      field.type === 'int' ||
      field.type === 'float' ||
      field.type === 'decimal' ||
      field.type === 'datetime' ||
      field.type === 'file'
    ) {
      field.isSingleLine = true;
    }

    if (!field.label) {
      field.label = superHumanize(field.key);
    }

    if (field.prefill && !field.choices) {
      field.choices = {};
    }

    if (field.choices) {
      field.type = 'pretty-select';
    }

    // This type of field is really a bug, but in the legacy editor, these
    // fields were treated as dict fields, not list fields.
    if (field.type === 'dict' && field.list === true) {
      delete field.list;
    }

    if (field.list === true) {
      field.type = 'list';
    }

    // Formatic doesn't pass choices onto children
    // so we must move choices to list's children
    if (field.type === 'list' && field.choices) {
      field.itemFields = [
        {
          type: 'unicode',
          shouldHideLabel: true,
          choices: field.choices,
        },
      ];
    }

    field = injectReplaceChoices(field, StaticReplaceChoices);

    return field;
  });

  return groupFields(formaticFields);
};

const normalizeDevFieldsForFormatic = (
  fields: FieldOrFunctionSchema[] = []
): Field[] => {
  let flattenFields = [];

  fields.forEach(field => {
    if (field.children) {
      const flattenChildren = field.children.map(child => ({
        ...child,
        list: false,
        parent_key: field.label || field.key,
      }));

      if (_.isEmpty(flattenChildren)) {
        flattenFields.push({
          parent_key: field.label || field.key || '',
          type: 'grouped-fields',
        });
      } else {
        flattenFields = flattenFields.concat(...flattenChildren);
      }
    } else {
      flattenFields.push(field);
    }
  });

  const preProcessed = flattenFields.map(field => {
    field = _.extend({}, field);

    if (field.dynamic) {
      field.prefill = field.dynamic;
    }

    if (field.search) {
      field.searchfill = field.search;
    }

    if (field.prefill || field.choices) {
      field.type = 'pretty-select';
    }

    if (field.dict) {
      field.type = 'dict';
    }

    return _.omit(field, ['dynamic', 'dict', 'search']);
  });

  return normalizeFieldsForFormatic(preProcessed);
};

const normalizeAuthFieldsForFormatic = (fields: Field[]): Field[] => {
  // auth fields are slightly different than regular fields
  // in that fields should default to required if the isRequired
  // key is not provided. This is how the backend expects auth fields.
  //  This ensures that every field has an isRequired key,
  // and if it doesn't have one, it defaults it to true.
  return normalizeFieldsForFormatic(fields).map(field => {
    return field.hasOwnProperty('required')
      ? field
      : { ...field, required: true };
  });
};

const searchOrWriteFields = (fields: Field[], node: FlowNode): Field[] => {
  if (node.type_of === 'search' || node.type_of === 'search_or_write') {
    // Don't allow any `convert_search` type fields.
    fields = (fields || []).filter(field => field.type !== 'convert_search');

    const writeGroupFields = [];

    // // Wrap write fields in a group so they can possibly have sub-groups.
    const writeGroupField: Field = {
      type: 'fields',
      isWriteContainer: true,
      key: '',
      label: '',
    };

    // While switching from search to search_or_write, might have fields without
    // proper from_search/from_write fields. Those are search fields.
    const searchFields = fields.filter(
      field => field.from_search || (!field.from_search && !field.from_write)
    );
    const writeFields: Field[] = fields.filter(
      field => field.from_write && !field.from_search
    );

    fields = searchFields;
    writeGroupField.fields = writeFields;
    if (writeFields.length > 0) {
      writeGroupFields.push(writeGroupField);
    }

    const convertSearchField = {
      type: 'convert-search',
      key: '',
      label: '',
    };

    fields = fields.concat(convertSearchField, writeGroupFields);
  }

  return fields;
};

/* given a prefill path like 'notebook.guid', split it into the proper pieces
 * and return a dict of it
 */
const splitPrefill = (prefillPath?: string) => {
  const pfdata =
    typeof prefillPath === 'string' ? prefillPath.split('.') : undefined;

  const action = get(pfdata, [0]);
  const targetKey = get(pfdata, [1]);
  const displayKeys = get(pfdata, [2, 'split', [',']]);

  return {
    action,
    targetKey,
    displayKeys,
  };
};

const splitSearchfill = (searchfillPath?: string) => {
  const sfdata =
    typeof searchfillPath === 'string' ? searchfillPath.split('.') : undefined;

  const action = get(sfdata, [0]);
  const targetKey = get(sfdata, [1]);

  return {
    action,
    targetKey,
  };
};

type DataType = {
  [s: string | typeof undefined | null]: any,
};

/* given a single object (of data) from an API, and a display key,
 * return a key which exists inside object `each` to use as the display key.
 * this method accounts for fallback options if `preferredKey` is null
 * note: preferredKey is usually the key defined inside the 'prefill' string
 * fallbackKey is returned after exausting all `defaults` possibilities
 */
const displayKey = (
  data: DataType,
  preferredKey: ?string,
  fallback: string | false
) => {
  const defaults = [
    'name',
    'Name',
    'display',
    'Display',
    'title',
    'Title',
    'subject',
    'Subject',
  ];
  if (
    exists(preferredKey) &&
    exists(data[preferredKey]) &&
    data[preferredKey] !== ''
  ) {
    return preferredKey;
  }
  if (fallback === false) {
    return null;
  }
  const key = _.find(defaults, defaultKey => {
    return exists(data[defaultKey]) && data[defaultKey] !== '';
  });
  if (exists(key)) {
    return key;
  }
  return fallback;
};

/* data:        raw data from a prefill `poll` against the action. should be a [] of {}
 * displayKey:  key name for display purposes in returned live data for the prefill
 * targetKey:   key name of the value from live data we ultimately store
 */
const prefillChoices = (
  data: { [string]: any },
  displayKeys?: string[],
  targetKey: string
) => {
  return _.map(data, item => {
    const value = item[targetKey];
    /* when multiple prefill 'display' keys are provided (the values after the second dot)
     * then we want to append all the display keys together with spaces in between
     */
    const label = Array.isArray(displayKeys)
      ? displayKeys.map(key => item[displayKey(item, key, targetKey)]).join(' ')
      : item[displayKey(item, null, targetKey)];
    if (label && _.isUndefined(value)) {
      return { label, value: '', sample: 'MISSING VALUE!' };
    }
    return { label, value, sample: value };
  }).filter(choice => !_.isUndefined(choice.value));
};

const fieldWithKey = (fields: Field[], key: string) => {
  return _.find(fields, field => field.key === key);
};

export const isCleanDropdown = (
  fields: Field[],
  fieldKey: string,
  needsMeta: {},
  prefill: string
) => {
  const fieldIndex = _.findIndex(fields, field => field.key === fieldKey);

  // Need a field to check.
  if (fieldIndex < 0) {
    return false;
  }

  const currentPrefill = get(needsMeta, [fieldKey, 'prefill']);
  // By using "prefill", we can differentiate between different Zapier Formatter types (date / time, utilities, etc.) which are all under the same "transform" fieldKey
  if (
    fieldKey === 'transform' &&
    prefill &&
    currentPrefill &&
    currentPrefill !== prefill
  ) {
    return false;
  }

  const loadStamp = get(needsMeta, [fieldKey, 'loadStamp'], 0);

  // If we haven't yet loaded it yet, it's dirty.
  if (loadStamp === 0) {
    return false;
  }

  // Unfortunately, some dropdowns depend on their own values, so if it's been
  // changed, it's dirty. It would be nice to make this optional someday,
  // because most dropdowns probably don't need this.
  if (loadStamp < get(needsMeta, [fieldKey, 'updateStamp'], 0)) {
    return false;
  }

  // Look at all preceding fields and see if they've been updated since they
  // were loaded.
  return fields.slice(0, fieldIndex).reduce((isCleanSoFar, currField) => {
    if (!isCleanSoFar) {
      return false;
    }
    const updateStamp = get(needsMeta, [currField.key || '', 'updateStamp'], 0);
    return loadStamp >= updateStamp;
  }, true);
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const choiceForValue = (value: string, choices: Array<FieldChoice>) => {
  choices = FormaticConfig.normalizeChoices(choices);
  return _.find(choices, choice => choice.value === value);
};

const isDropdown = (field?: Field) => {
  return !!field && (!!field.choices || field.prefill);
};

// Returns true if this is node is 'empty', ie
// the user has not even selected the app yet.
const isEmptyNode = node => !node.type_of || !node.selected_api;

const getChoiceLabel = (node, services, actionLabelFn) => {
  const actionLabel =
    _.get(node, ['meta', 'stepTitle']) || actionLabelFn(node, services);
  const service = NodeUtils.service(node, services) || {};
  return actionLabel || service.name;
};

const getSpecialChoices = (flow, node, nodeGives, stepNumber, groupId) => {
  const choiceProps = {
    stepNumber,
    label: `Missing choices? Complete step ${stepNumber}`,
    flowId: flow.id,
    groupId,
    nodeId: node.id,
  };

  if (
    isEmptyNode(node) ||
    (_.isEmpty(nodeGives) &&
      !NodeUtils.hasSampleData(flow.nodes[node.id], flow.taskState))
  ) {
    return [
      {
        ...choiceProps,
        action: 'complete-step',
      },
    ];
  } else {
    return [
      {
        ...choiceProps,
        action: 'return-to-sample',
      },
    ];
  }
};

type NodeLabelFn = (node: FlowNode, services: ServicesMap) => string;

export const givesToChoicesWithSections = (
  gives: Give[],
  flow: Flow,
  services: ServicesMap,
  currentNodeId: ID,
  actionLabelFn: NodeLabelFn = NodeLabels.actionLabel
) => {
  const ancestors = getAncestors(flow.nodes, currentNodeId);
  const givableAncestors = ancestors.filter(
    node => !isPathsGroupNode(node) && !isPathNode(node)
  );

  return _.reduce(
    givableAncestors,
    (choices, node) => {
      const label = isEmptyNode(node)
        ? 'Empty Step'
        : getChoiceLabel(node, services, actionLabelFn);
      const stepNumber = getStepLabel({
        flowId: flow.id,
        nodes: flow.nodes,
        nodeId: node.id,
      });
      const nodeGives = _.filter(gives, give => give.node_id === node.id);
      const isStepInPath = isNodeInPath(flow.id, flow.nodes, node.id);

      // Paths
      const rootNode = getRootNodeOfPath(flow.id, flow.nodes, node.id);
      const groupId = `${rootNode.id}-${getStepIndex(
        flow.id,
        flow.nodes,
        node.id
      ) + 1}`;
      const { emoji, color, name } = getPathAttrs(rootNode);
      const lightPathColor = getPathSelectedBackgroundColor(color);
      const doesPathHeaderExist = choices.some(
        choice => choice.rootNodeId && choice.rootNodeId === rootNode.id
      );

      const pathHeader =
        isStepInPath && !doesPathHeaderExist
          ? {
              isPathHeader: true,
              rootNodeId: rootNode.id,
              groupId,
              emoji,
              label: `Path: ${name}`,
              color,
              lightPathColor,
              sectionKey: null,
              stepNumber,
              notClickable: true,
              isNestedPath: isNestedPath(flow.id, flow.nodes, node.id),
              shouldShowInSearch: true,
            }
          : [];

      // accordion section header, ie '1 - New Email'
      const sectionHeader = {
        label,
        selectedApi: node.selected_api,
        sectionKey: groupId,
        groupId,
        stepNumber,
        visibleStepNumber: getStepLabel({
          flowId: flow.id,
          nodes: flow.nodes,
          nodeId: node.id,
          options: { showPathName: false },
        }),
        isStepInPath,
        lightPathColor,
      };

      const stepChoices = givesToReplaceChoices(flow, node, nodeGives).map(
        choice => ({
          stepNumber,
          groupId,
          ...choice,
        })
      );

      const specialChoices = getSpecialChoices(
        flow,
        node,
        nodeGives,
        stepNumber,
        groupId
      );

      return choices.concat(
        pathHeader,
        sectionHeader,
        stepChoices,
        specialChoices
      );
    },
    []
  );
};

const isListItem = (field: Field) =>
  field.parent && field.parent.type === 'list';

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

// Convert some value to a field.
const valueToField = value => {
  if (_.isBoolean(value)) {
    return {
      type: 'pretty-boolean',
      readOnly: true,
    };
  } else if (_.isArray(value)) {
    return {
      type: 'list',
      itemFields: [
        {
          type: 'unicode',
          isSingleLine: true,
          readOnly: true,
        },
      ],
      readOnly: true,
    };
  } else if (_.isObject(value)) {
    return {
      type: 'dict',
      itemFields: [
        {
          type: 'unicode',
          isSingleLine: true,
          readOnly: true,
        },
      ],
      readOnly: true,
    };
  } else {
    return {
      type: 'pretty-text',
      isSingleLine: !_.isString(value) || value.indexOf('\n') < 0,
      readOnly: true,
    };
  }
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

// Some formatic fields are wrapped; flatten those.
const fieldKeys = (fields, { excludeOptional = false } = {}) => {
  if (!_.isArray(fields)) {
    return [];
  }
  return _.flatten(
    fields.map(field => {
      if (excludeOptional && !field.required) {
        return [];
      }
      if (field.key) {
        return field.key;
      }
      if (field.fields) {
        return fieldKeys(field.fields);
      }
      return [];
    })
  );
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.

// If there are params without fields, make up some fields on the fly.
const buildExtraFields = (fields: Array<Field>, params: {}) => {
  if (!_.isArray(fields) || !_.isObject(params)) {
    return fields;
  }
  const fieldSet = _.fromPairs(fieldKeys(fields).map(key => [key, true]));
  const extraParams = _.fromPairs(
    Object.keys(params)
      .filter(paramKey => !fieldSet[paramKey])
      .map(paramKey => [paramKey, params[paramKey]])
  );
  const extraFields = _.map(extraParams, (value, key) => {
    const fieldWithoutKey = valueToField(value);
    return { ...fieldWithoutKey, key };
  });
  if (extraFields.length > 0) {
    return extraFields;
  }
  return null;
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const hasMissingFields = (fields: Field[], params: {} = {}) => {
  if (!_.isArray(fields)) {
    return false;
  }
  const hasMissingFieldsPlaceholder = !!_.get(
    _.last(fields),
    'isMissingFieldsPlaceholder'
  );
  if (hasMissingFieldsPlaceholder) {
    const requiredFieldKeys = fieldKeys(fields, { excludeOptional: true });
    const requiredParams = _.pick(params, requiredFieldKeys);
    const cleanRequiredParams = cleanObjectOfEmptyValues(requiredParams);
    const cleanRequiredFieldKeys = Object.keys(cleanRequiredParams);
    // If there are still some required fields to fill out, then our missing
    // fields are probably the result of missing params in the custom fields
    // endpoint, so ignore till those are filled in.
    if (cleanRequiredFieldKeys.length < requiredFieldKeys.length) {
      return false;
    }
  }
  return hasMissingFieldsPlaceholder;
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const retrieveMissingFields = (fields: Array<Field>, params: {} = {}) => {
  return hasMissingFields(fields, params) ? _.last(fields) : false;
};

// !!! IMPORTANT !!!
// This function affects the global flows redux state. You
// may need to make the same changes to both the beta-editor
// and classic-editor code. Edit with caution and test thoroughly.
const removeMissingFields = (fields: Array<Field>): Array<Field> => {
  if (_.get(_.last(fields), 'isMissingFieldsPlaceholder')) {
    return fields.slice(0, fields.length - 1);
  }
  return fields;
};

const canInvalidateNeeds = (field: Field) => {
  if (field.alters_custom_fields === false) {
    // Always respect field manually opting out.
    return false;
  } else if (field.alters_custom_fields || field.prefill) {
    // Field has opted in or is a dynamic dropdown.
    return true;
  } else if (
    field.parent &&
    (field.parent.alters_custom_fields || field.parent.prefill)
  ) {
    // Parent field has opted in or is a dynamic dropdown.
    // For example: this field is one list entry that has changed in a dynamic dropdown.
    return true;
  }

  return false;
};

const isFieldOfType = (type: string, field: Field) => {
  const fieldType = _.get(field, ['type'], null);
  const zapierFieldType = _.get(field, ['zapierType'], null);

  return fieldType === type || zapierFieldType === type;
};

const isFieldOfTypes = (types: string[], field: Field) =>
  types.some(type => isFieldOfType(type, field));

const isBoolField = _.partial(isFieldOfTypes, ['bool', 'boolean']);
const isDictField = _.partial(isFieldOfType, 'dict');
const isListField = _.partial(isFieldOfType, 'list');
const isPrettyPillsField = _.partial(isFieldOfTypes, [
  'pretty-text',
  'PrettyText',
  'text',
  'unicode',
  'datetime',
]);
const isPrettySelectField = _.partial(isFieldOfType, 'pretty-select');

const isCustomValueField = field => {
  return (
    isPrettyPillsField(field) &&
    _.get(field, ['parent', 'type']) === 'pretty-select' &&
    _.get(field, ['parent', 'parent', 'type']) === 'list'
  );
};

const getOwnerField = field => {
  if (isCustomValueField(field)) {
    return _.get(field, ['parent', 'parent']);
  }
  if (
    field.parent &&
    field.parent.type !== 'grouped-fields' &&
    field.parent.type !== 'fields'
  ) {
    return field.parent;
  }
  return field;
};

const getInvalidMappings = (field: Field) => {
  const owner = getOwnerField(field);
  return _.get(
    field.meta,
    ['node', 'meta', 'validation', 'params', 'invalid_mappings', owner.key],
    []
  );
};

const hasInvalidPrettyPills = (field: Field) => {
  if (!isPrettyPillsField(field)) {
    return false;
  }

  const invalidMappings = getInvalidMappings(field);
  return _.some(
    invalidMappings,
    m => _.get(field, 'value', '').indexOf(m) !== -1
  );
};

const bothAreArrays = (obj1: {} | [], obj2: {} | []) =>
  _.isArray(obj1) && _.isArray(obj2);

const getFieldKey = (event: { path: Array<*> }) => _.head(event.path);

const normalizeChoicesToObject = (choices: Array<FieldChoice>) => {
  return FormaticConfig.normalizeChoices(choices).reduce(
    (obj, choice) => ({
      ...obj,
      [choice.value]: choice.label,
    }),
    {}
  );
};

const mapKeysToChoices = (
  event: {},
  params: {},
  dynamicChoices: Array<FieldChoice>
) => {
  const choices = normalizeChoicesToObject(
    _.get(event, ['field', 'choices'], {})
  );

  if (!_.isEmpty(choices)) {
    return _.map(params, key => choices[key] || '');
  }
  if (!_.isEmpty(dynamicChoices)) {
    return _.map(params, key => {
      const choice = _.find(dynamicChoices, c => c.value === key);
      return (choice && choice.label) || '';
    });
  }
  // If we don't have any choices or dynamicChoices, don't override
  // the existing parammap with an empty one. That causes weird things
  // like all the existing choices in a prefill list to change their labels
  // to 'Use a Custom Value'.
  return params;
};

// *Mutates* meta.parammap, using choices (for static dropdowns)
// or dynamicChoices (for dynamic dropdowns).
const remapChoiceValues = ({
  meta,
  node = {},
  params = {},
  event = { path: [] },
  dynamicChoices = [],
}: {
  meta: NodeMeta,
  node: {},
  params: {},
  event?: { path: Array<*> },
  dynamicChoices: Array<FieldChoice>,
}) => {
  const fieldKey = getFieldKey(event);
  const newParams = params && params[fieldKey] ? params[fieldKey] : {};
  const oldParams = _.get(node, ['params', fieldKey], []);
  if (
    Array.isArray(newParams) &&
    !Array.isArray(_.get(meta, ['parammap', fieldKey]))
  ) {
    meta.parammap[fieldKey] = [];
  }
  if (!_.isEqual(oldParams, newParams)) {
    const newParamMap = mapKeysToChoices(event, newParams, dynamicChoices);
    if (newParamMap) {
      meta.parammap[fieldKey] = mapKeysToChoices(
        event,
        newParams,
        dynamicChoices
      );
    }
  }
};

export const filterCannotContinue = (node: FlowNode) => {
  const filterCriteria = _.get(node, ['params', 'filter_criteria'], []);
  // if there are any items that are valid, it can continue / should return false
  return _.isEmpty(filterCriteria)
    ? true
    : _.some(filterCriteria, criteria => {
        return !criteria.key || !criteria.match;
      });
};

export const isFilterField = (field: Field) => field.key === 'filter_criteria';

// Path-specific
export const isColorField = (field: Field) => field.key === 'color';
export const isEmojiField = (field: Field) => field.key === 'emoji';
export const isNameField = (field: Field) => field.key === 'name';

type UnimportantActionFieldsKey = {
  selected_api: string,
  type_of: FlowNodeType,
  action: string,
};
const getUnimportantActionFields = (key: UnimportantActionFieldsKey) =>
  _.find(
    servicesWithUnmappedFields,
    ({ selected_api, type_of, action }) =>
      key.selected_api === selected_api &&
      key.type_of === type_of &&
      key.action === action
  ) || {};

export const hasHiddenUnimportantActionFields = (
  key: UnimportantActionFieldsKey
) => !_.isEmpty(getUnimportantActionFields(key));

export const isHiddenUnimportantActionField = (
  field: Field,
  key: UnimportantActionFieldsKey
) => {
  const unimportantActionFields = getUnimportantActionFields(key);
  return _.get(unimportantActionFields, ['hide_fields', field.key]);
};

export const shouldSaveOnValueChanged = (oldValue: *, newValue: *) => {
  if (_.isObject(newValue) && _.isObject(oldValue)) {
    // Upgrading from 0.83.0 to 0.84.0 caused Flow
    // to error out here. Flow is not aware that newValue
    // or oldValue are objects from the resolution of the conditional
    // $FlowFixMe
    const newLength = Object.keys(newValue).length;
    const oldLength = Object.keys(oldValue).length;
    // We need to check for removal of object or array items,
    // because there's no blur event to save those.
    // No blur event for adding items either, but those
    // will be edited and then blurred (or removed if blank anyway).
    if (newLength < oldLength) {
      return true;
    }
    // Otherwise, check deep equality to catch order changes
    // This will catch order changes in arrays but not objects
    // because objects are not logically ordered
    else {
      return !_.isEqual(oldValue, newValue);
    }
  }
  return false;
};

const searchGivesFromTag = (nodeId: ID, flow: Flow, tag: string) => {
  const gives = _.get(flow, ['givesAll', nodeId]);
  return _.find(gives, ({ key }) => key === tag);
};

const FieldUtils = {
  searchGivesFromTag,
  transformGives,
  giveLabel,
  humanizeGiveLabel,
  givesToReplaceChoices,
  givesToChoices,
  givesToChoicesWithSections,
  givesLabelMap,
  hasGivesPrefill,
  injectReplaceChoices,
  normalizeFieldsForFormatic,
  normalizeDevFieldsForFormatic,
  normalizeAuthFieldsForFormatic,
  searchOrWriteFields,
  splitPrefill,
  splitSearchfill,
  displayKey,
  prefillChoices,
  fieldWithKey,
  isCleanDropdown,
  choiceForValue,
  isDropdown,
  formatSampleValue,
  formatSampleValueOfGive,
  isListItem,
  buildExtraFields,
  hasMissingFields,
  retrieveMissingFields,
  removeMissingFields,
  canInvalidateNeeds,
  hasInvalidPrettyPills,
  bothAreArrays,
  getFieldKey,
  mapKeysToChoices,
  remapChoiceValues,
  isFieldOfType,
  isFieldOfTypes,
  isBoolField,
  isPrettyPillsField,
  isPrettySelectField,
  isListField,
  isDictField,
  filterCannotContinue,
  getInvalidMappings,
  normalizeChoicesToObject,
  hasHiddenUnimportantActionFields,
  isHiddenUnimportantActionField,
  shouldSaveOnValueChanged,
};

export default FieldUtils;
